import { useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { api, type CapacitySetting, type SingleTaskRule, type ResourceSettings } from '../api/client'

const STAGE_LABELS: Record<string, string> = {
  name:          'ネーム',
  name_revision: 'ネーム修正',
  sketch:        '下書き',
  inking:        'ペン入れ',
  background:    '背景',
  finishing:     '仕上げ',
}

const WORKER_TYPE_LABELS: Record<string, string> = {
  artist:    '漫画家',
  assistant: 'アシスタント',
}

const UNIT_LABELS: Record<string, string> = {
  pages: 'P/日',
  days:  '日',
}

export default function ProductionPlanningSettings() {
  const [capacitySettings, setCapacitySettings] = useState<CapacitySetting[]>([])
  const [singleTaskRules, setSingleTaskRules]   = useState<SingleTaskRule[]>([])
  const [loading, setLoading]   = useState(true)
  const [saving, setSaving]     = useState(false)
  const [message, setMessage]   = useState<string | null>(null)

  const [resourceSettings, setResourceSettings]   = useState<ResourceSettings | null>(null)
  const [resourceSaving, setResourceSaving]       = useState(false)
  const [resourceMessage, setResourceMessage]     = useState<string | null>(null)

  useEffect(() => {
    api.productionPlanning.getSettings().then((data) => {
      setCapacitySettings(data.capacity_settings)
      setSingleTaskRules(data.single_task_rules)
    }).catch((err) => {
      setMessage(`読み込みエラー: ${err.message}`)
    }).finally(() => setLoading(false))

    api.productionPlanning.getResourceSettings().then((data) => {
      setResourceSettings(data)
    }).catch((err) => {
      setResourceMessage(`読み込みエラー: ${err.message}`)
    })
  }, [])

  const resourceValid =
    resourceSettings !== null &&
    Number.isInteger(resourceSettings.windows_capacity) &&
    Number.isInteger(resourceSettings.mac_capacity) &&
    resourceSettings.windows_capacity >= 0 && resourceSettings.windows_capacity <= 50 &&
    resourceSettings.mac_capacity >= 0 && resourceSettings.mac_capacity <= 50

  const handleSaveResource = async () => {
    if (!resourceSettings || !resourceValid) return
    setResourceSaving(true)
    setResourceMessage(null)
    try {
      const res = await api.productionPlanning.updateResourceSettings({
        windows_capacity: resourceSettings.windows_capacity,
        mac_capacity:     resourceSettings.mac_capacity,
        is_enabled:       resourceSettings.is_enabled,
        notes:            resourceSettings.notes,
      })
      setResourceSettings(res.resource_settings)
      setResourceMessage('保存しました')
    } catch (err: any) {
      setResourceMessage(`保存エラー: ${err.message}`)
    } finally {
      setResourceSaving(false)
    }
  }

  const updateCap = (id: string, field: keyof CapacitySetting, value: number) => {
    setCapacitySettings((prev) =>
      prev.map((s) => s.id === id ? { ...s, [field]: value } : s),
    )
  }

  const updateRule = (id: string, field: keyof SingleTaskRule, value: string | number | null) => {
    setSingleTaskRules((prev) =>
      prev.map((r) => r.id === id ? { ...r, [field]: value } : r),
    )
  }

  const handleSave = async () => {
    setSaving(true)
    setMessage(null)
    try {
      await api.productionPlanning.updateSettings({
        capacity_settings: capacitySettings.map((s) => ({
          id:               s.id,
          normal_capacity:  s.normal_capacity,
          rush_capacity:    s.rush_capacity,
          can_work_weekends: s.can_work_weekends,
          can_parallel:     s.can_parallel,
          is_active:        s.is_active,
        })),
        single_task_rules: singleTaskRules.map((r) => ({
          id:            r.id,
          label:         r.label,
          default_days:  r.default_days,
          has_page_quota: r.has_page_quota,
          note:          r.note,
          is_active:     r.is_active,
        })),
      })
      setMessage('保存しました')
    } catch (err: any) {
      setMessage(`保存エラー: ${err.message}`)
    } finally {
      setSaving(false)
    }
  }

  if (loading) return <div className="loading">LOADING...</div>

  return (
    <div>
      {/* パンくず */}
      <div style={{ marginBottom: 12, fontSize: 12, color: 'var(--ink-3)', display: 'flex', alignItems: 'center', gap: 6 }}>
        <Link to="/works" style={{ color: 'var(--ink-3)', textDecoration: 'none' }}>作品・話数</Link>
        <span>›</span>
        <span>制作進行設定</span>
      </div>

      {/* 保存ボタン + メッセージ */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 16 }}>
        <button
          className="btn btn-primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? '保存中...' : '一括保存'}
        </button>
        {message && (
          <span style={{
            fontSize: 13,
            color: message.startsWith('保存しました') ? 'var(--green)' : 'var(--red)',
          }}>
            {message}
          </span>
        )}
      </div>

      {/* 制作環境設定（Windows/Mac 同時作業可能人数） */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header">
          <span className="card-title">制作環境設定</span>
        </div>
        <div style={{ padding: '16px' }}>
          <div style={{ fontSize: 12, color: 'var(--ink-3)', marginBottom: 12 }}>
            シフト確定画面のPC設備数チェックで使用する、同時作業可能人数の上限です。
          </div>
          {resourceSettings === null ? (
            <div className="text-muted" style={{ fontSize: 13 }}>読み込み中...</div>
          ) : (
            <>
              <div style={{ display: 'flex', gap: 24, marginBottom: 12, flexWrap: 'wrap' }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Windows作業可能人数</label>
                  <input
                    type="number"
                    min="0"
                    max="50"
                    step="1"
                    value={resourceSettings.windows_capacity}
                    onChange={(e) => setResourceSettings({
                      ...resourceSettings,
                      windows_capacity: e.target.value === '' ? NaN : Number(e.target.value),
                    })}
                    style={{ width: 100 }}
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>Mac作業可能人数</label>
                  <input
                    type="number"
                    min="0"
                    max="50"
                    step="1"
                    value={resourceSettings.mac_capacity}
                    onChange={(e) => setResourceSettings({
                      ...resourceSettings,
                      mac_capacity: e.target.value === '' ? NaN : Number(e.target.value),
                    })}
                    style={{ width: 100 }}
                  />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label>有効</label>
                  <div style={{ paddingTop: 6 }}>
                    <input
                      type="checkbox"
                      checked={resourceSettings.is_enabled === 1}
                      onChange={(e) => setResourceSettings({
                        ...resourceSettings,
                        is_enabled: e.target.checked ? 1 : 0,
                      })}
                    />
                  </div>
                </div>
              </div>
              <div className="form-group" style={{ marginBottom: 12 }}>
                <label>備考</label>
                <input
                  type="text"
                  value={resourceSettings.notes ?? ''}
                  onChange={(e) => setResourceSettings({ ...resourceSettings, notes: e.target.value || null })}
                  placeholder="（任意）"
                  style={{ width: 320 }}
                />
              </div>
              {!resourceValid && (
                <div style={{ fontSize: 12, color: 'var(--red)', marginBottom: 8 }}>
                  台数は0〜50の整数で入力してください
                </div>
              )}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                <button
                  className="btn btn-primary btn-sm"
                  onClick={handleSaveResource}
                  disabled={resourceSaving || !resourceValid}
                >
                  {resourceSaving ? '保存中...' : '保存'}
                </button>
                {resourceSettings.updated_at && (
                  <span style={{ fontSize: 11, color: 'var(--ink-3)' }}>
                    更新日時: {resourceSettings.updated_at}
                  </span>
                )}
                {resourceMessage && (
                  <span style={{
                    fontSize: 13,
                    color: resourceMessage.startsWith('保存しました') ? 'var(--green)' : 'var(--red)',
                  }}>
                    {resourceMessage}
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      </div>

      {/* 工程別キャパ設定 */}
      <div className="card" style={{ marginBottom: 20 }}>
        <div className="card-header">
          <span className="card-title">工程別キャパ設定</span>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>工程</th>
                <th>担当</th>
                <th>単位</th>
                <th>通常キャパ</th>
                <th>緊急キャパ</th>
                <th>土日作業可</th>
                <th>並行可</th>
                <th>有効</th>
              </tr>
            </thead>
            <tbody>
              {capacitySettings.map((s) => (
                <tr key={s.id} style={{ opacity: s.is_active ? 1 : 0.5 }}>
                  <td style={{ fontWeight: 500 }}>
                    {STAGE_LABELS[s.stage_key] ?? s.stage_key}
                  </td>
                  <td>
                    <span className={`badge ${s.worker_type === 'artist' ? 'badge-blue' : 'badge-green'}`} style={{ fontSize: 11 }}>
                      {WORKER_TYPE_LABELS[s.worker_type] ?? s.worker_type}
                    </span>
                  </td>
                  <td style={{ fontSize: 12, color: 'var(--ink-3)' }}>
                    {UNIT_LABELS[s.unit] ?? s.unit}
                  </td>
                  <td>
                    <input
                      type="number"
                      step="0.5"
                      min="0"
                      value={s.normal_capacity}
                      onChange={(e) => updateCap(s.id, 'normal_capacity', Number(e.target.value))}
                      style={{ width: 80, textAlign: 'right' }}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      step="0.5"
                      min="0"
                      value={s.rush_capacity}
                      onChange={(e) => updateCap(s.id, 'rush_capacity', Number(e.target.value))}
                      style={{ width: 80, textAlign: 'right' }}
                    />
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={s.can_work_weekends === 1}
                      onChange={(e) => updateCap(s.id, 'can_work_weekends', e.target.checked ? 1 : 0)}
                    />
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={s.can_parallel === 1}
                      onChange={(e) => updateCap(s.id, 'can_parallel', e.target.checked ? 1 : 0)}
                    />
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={s.is_active === 1}
                      onChange={(e) => updateCap(s.id, 'is_active', e.target.checked ? 1 : 0)}
                    />
                  </td>
                </tr>
              ))}
              {capacitySettings.length === 0 && (
                <tr>
                  <td colSpan={8} className="text-muted" style={{ textAlign: 'center', padding: 24 }}>
                    設定データがありません。Migration を適用してください。
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* 単体作業ルール */}
      <div className="card">
        <div className="card-header">
          <span className="card-title">単体作業ルール</span>
        </div>
        <div className="table-wrap">
          <table>
            <thead>
              <tr>
                <th>作業タイプ</th>
                <th>表示名</th>
                <th>標準日数</th>
                <th>ページノルマ有</th>
                <th>メモ</th>
                <th>有効</th>
              </tr>
            </thead>
            <tbody>
              {singleTaskRules.map((r) => (
                <tr key={r.id} style={{ opacity: r.is_active ? 1 : 0.5 }}>
                  <td style={{ fontSize: 12, color: 'var(--ink-3)', fontFamily: 'monospace' }}>
                    {r.work_type}
                  </td>
                  <td>
                    <input
                      type="text"
                      value={r.label}
                      onChange={(e) => updateRule(r.id, 'label', e.target.value)}
                      style={{ width: 160 }}
                    />
                  </td>
                  <td>
                    <input
                      type="number"
                      step="0.5"
                      min="0.5"
                      value={r.default_days}
                      onChange={(e) => updateRule(r.id, 'default_days', Number(e.target.value))}
                      style={{ width: 80, textAlign: 'right' }}
                    />
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={r.has_page_quota === 1}
                      onChange={(e) => updateRule(r.id, 'has_page_quota', e.target.checked ? 1 : 0)}
                    />
                  </td>
                  <td>
                    <input
                      type="text"
                      value={r.note ?? ''}
                      onChange={(e) => updateRule(r.id, 'note', e.target.value || null)}
                      style={{ width: 200 }}
                      placeholder="（メモ）"
                    />
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <input
                      type="checkbox"
                      checked={r.is_active === 1}
                      onChange={(e) => updateRule(r.id, 'is_active', e.target.checked ? 1 : 0)}
                    />
                  </td>
                </tr>
              ))}
              {singleTaskRules.length === 0 && (
                <tr>
                  <td colSpan={6} className="text-muted" style={{ textAlign: 'center', padding: 24 }}>
                    設定データがありません。Migration を適用してください。
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      <div style={{ marginTop: 16 }}>
        <button
          className="btn btn-primary"
          onClick={handleSave}
          disabled={saving}
        >
          {saving ? '保存中...' : '一括保存'}
        </button>
      </div>
    </div>
  )
}
